import type { FlightSegment } from "../api";
import type { TZ } from "../lib/time";
import { formatDate } from "../lib/time";
import { Button } from "./ui/button";
import { X } from "lucide-react";

interface Props {
  hex: string;
  segments: FlightSegment[];
  onSegmentSelect: (seg: FlightSegment) => void;
  onClose: () => void;
  tz: TZ;
}

function formatDuration(ms: number): string {
  const totalSec = Math.floor(ms / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m`;
  return "<1m";
}

export default function FlightList({ hex, segments, onSegmentSelect, onClose, tz }: Props) {
  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between px-3 py-2.5 bg-card/80 border-b border-border shrink-0">
        <span className="font-bold text-sm text-primary">{hex.toUpperCase()}</span>
        <Button variant="ghost" size="icon" className="h-6 w-6 text-muted-foreground hover:text-foreground" onClick={onClose}>
          <X className="h-4 w-4" />
        </Button>
      </div>
      <div className="flex-1 overflow-y-auto">
        {segments.length === 0 ? (
          <p className="p-4 text-sm text-muted-foreground">No flights found for this aircraft.</p>
        ) : (
          segments.map((seg, i) => {
            const label = seg.callsign ?? "(no callsign)";
            const date = formatDate(seg.start_at, tz);
            const duration = formatDuration(seg.end_at - seg.start_at);
            return (
              <button
                key={i}
                className="w-full text-left px-3 py-2.5 border-b border-border hover:bg-muted/50 transition-colors"
                onClick={() => onSegmentSelect(seg)}
              >
                <div className="font-semibold text-sm text-foreground">{label}</div>
                <div className="text-xs text-muted-foreground mt-0.5">
                  {date} · {duration} · {seg.position_count} pts
                </div>
              </button>
            );
          })
        )}
      </div>
    </div>
  );
}
